import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Loader2, ChevronDown, ChevronUp, ExternalLink, CheckCircle2, ListChecks, Clock,
} from 'lucide-react';
import { api } from '@/lib/api';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';

const PAGE_SIZE = 20;

// Sprint Preparo Phase 2 — scalable questionnaire picker (locked architecture).
// Lives INSIDE the Evaluation Wizard; Library remains the source of truth;
// server-side search + pagination on the existing GET /admin/questionnaires.
export function QuestionnairePicker({ suiteCode, value, onSelect }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const [q, setQ] = useState('');
  const [language, setLanguage] = useState('all');
  const [type, setType] = useState('all');
  const [level, setLevel] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [vocab, setVocab] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const timer = useRef(null);

  useEffect(() => { adminApi.getLibraryVocab().then(setVocab).catch(() => setVocab(null)); }, []);

  const load = useCallback((params) => {
    setLoading(true);
    api.get('/admin/questionnaires', { params })
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = { page, page_size: PAGE_SIZE, status: 'published', suite: suiteCode };
    if (q.trim()) params.q = q.trim();
    if (language !== 'all') params.language = language;
    if (type !== 'all') params.evaluation_type = type;
    if (level !== 'all') params.level = level;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(params), q ? 300 : 0);
    return () => clearTimeout(timer.current);
  }, [q, language, type, level, page, suiteCode, load]);

  const toggleExpand = (item) => {
    if (expandedId === item.id) { setExpandedId(null); return; }
    setExpandedId(item.id); setDetail(null);
    adminApi.getQuestionnaire(item.id).then(setDetail).catch(() => setDetail(null));
  };

  const vocabOpts = (field) => (vocab?.[field] || []).map((v) => v.code || v.value || v);
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="rounded-lg border border-border" data-testid="questionnaire-picker">
      <div className="p-2.5 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder={t('qPicker.searchPh', { defaultValue: 'Search by name or code…' })}
            className="pl-8 h-9"
            data-testid="qpicker-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            ['language', language, setLanguage, t('qPicker.language', { defaultValue: 'Language' })],
            ['evaluation_type', type, setType, t('qPicker.type', { defaultValue: 'Type' })],
            ['level', level, setLevel, t('qPicker.level', { defaultValue: 'Difficulty' })],
          ].map(([field, val, setVal, label]) => (
            <Select key={field} value={val} onValueChange={(v) => { setVal(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-auto min-w-28 text-xs" data-testid={`qpicker-filter-${field}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{label}: {t('qPicker.all', { defaultValue: 'All' })}</SelectItem>
                {vocabOpts(field).map((o) => (
                  <SelectItem key={o} value={o}>{tData(o)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          <Badge variant="outline" className="h-8 px-2.5 text-xs tabular-nums ml-auto" data-testid="qpicker-total">
            {t('qPicker.results', { defaultValue: '{{n}} results', n: data.total })}
          </Badge>
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto divide-y divide-border" data-testid="qpicker-list">
        {loading ? (
          <div className="p-6 flex items-center justify-center text-muted-foreground text-sm" data-testid="qpicker-loading">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground" data-testid="qpicker-empty">
            {t('configureAssignment.noCompatible', { defaultValue: 'No published questionnaire is compatible with this suite.' })}
          </div>
        ) : data.items.map((item) => {
          const selected = value?.id === item.id;
          const expanded = expandedId === item.id;
          return (
            <div key={item.id} className={selected ? 'bg-primary/5' : ''}>
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={() => onSelect(item)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`qpicker-item-${item.id}`}
                >
                  <p className="text-sm font-medium truncate flex items-center gap-1.5">
                    {selected && <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />}
                    {item.name}
                  </p>
                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
                    <span className="font-mono">{item.code}</span>
                    {item.version_number != null && <span>v{item.version_number}</span>}
                    {item.question_count != null && (
                      <span className="inline-flex items-center gap-0.5"><ListChecks className="h-3 w-3" />{t('qPicker.questions', { defaultValue: '{{n}} questions', n: item.question_count })}</span>
                    )}
                    {item.estimated_duration_minutes != null && (
                      <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />~{item.estimated_duration_minutes} min</span>
                    )}
                    {item.language && <span className="uppercase">{item.language}</span>}
                  </p>
                </button>
                <a
                  href={`/admin/questionnaires?q=${encodeURIComponent(item.code || item.name)}`}
                  target="_blank" rel="noreferrer"
                  title={t('qPicker.openInLibrary', { defaultValue: 'Open in Library' })}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`qpicker-library-link-${item.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button type="button" onClick={() => toggleExpand(item)} className="text-muted-foreground hover:text-foreground" data-testid={`qpicker-expand-${item.id}`}>
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>
              {expanded && (
                <div className="px-3 pb-3 text-xs text-muted-foreground space-y-1" data-testid={`qpicker-detail-${item.id}`}>
                  {detail === null ? (
                    <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}</span>
                  ) : (
                    <>
                      {detail?.description && <p>{detail.description}</p>}
                      {detail?.objective && <p><span className="font-semibold">{t('qPicker.objective', { defaultValue: 'Objective' })}:</span> {detail.objective}</p>}
                      {(detail?.evaluation_type || detail?.professional_area) && (
                        <p className="capitalize">{[detail.evaluation_type, detail.professional_area].filter(Boolean).map(tData).join(' · ')}</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border">
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="qpicker-prev">
            {t('common.previous', { defaultValue: 'Previous' })}
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">{page} / {totalPages}</span>
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="qpicker-next">
            {t('common.next', { defaultValue: 'Next' })}
          </Button>
        </div>
      )}
    </div>
  );
}

export default QuestionnairePicker;
